'use client'

import {
    API_ADMIN_COMPANIES_URL,
    API_COMPANY_DASHBOARD_STATS_URL,
    API_COMPANY_PREVIEW_URL,
    API_COMPANY_PROFILE_PUBLISH_URL,
    API_COMPANY_PROFILE_URL,
    apiAdminCompanyDashboardStatsUrl,
    apiAdminCompanyPreviewUrl,
    apiAdminCompanyProfileUrl,
    apiAdminCompanyPublishUrl,
} from '@/consts/baseUrls'
import { useAuth } from '@/provider/AuthContext'
import axios from '@/utils/axios'
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react'

type CompanySummary = {
    id: string
    organizationId: string
    name: string
    industry: string | null
}

type AdminContextValue = {
    isWalterAdmin: boolean
    canEdit: boolean
    canPublish: boolean
    companies: CompanySummary[]
    isLoadingCompanies: boolean
    selectedOrganizationId: string | null
    selectedCompanyName: string | null
    selectCompany: (organizationId: string) => void
    profileUrl: string
    publishUrl: string
    dashboardStatsUrl: string
    previewInfoUrl: string
}

const AdminContext = createContext<AdminContextValue | undefined>(undefined)

export const AdminProvider = ({ children }: { children: ReactNode }) => {
    const { user, isLoading: isAuthLoading } = useAuth()
    const isWalterAdmin = !isAuthLoading && user?.role === 'WALTER_ADMIN'

    const [companies, setCompanies] = useState<CompanySummary[]>([])
    const [isLoadingCompanies, setIsLoadingCompanies] = useState(false)
    const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null)

    useEffect(() => {
        if (isAuthLoading || !isWalterAdmin) return

        const loadCompanies = async () => {
            setIsLoadingCompanies(true)
            try {
                const response = await axios.get<CompanySummary[]>(API_ADMIN_COMPANIES_URL, {
                    withCredentials: true,
                })
                setCompanies(response.data)
                if (response.data.length > 0 && !selectedOrganizationId) {
                    setSelectedOrganizationId(response.data[0]!.organizationId)
                }
            } finally {
                setIsLoadingCompanies(false)
            }
        }

        void loadCompanies()
    }, [isAuthLoading, isWalterAdmin]) // eslint-disable-line react-hooks/exhaustive-deps

    const selectCompany = useCallback((organizationId: string) => {
        setSelectedOrganizationId(organizationId)
    }, [])

    const selectedCompanyName = companies.find((c) => c.organizationId === selectedOrganizationId)?.name ?? null

    const profileUrl =
        isWalterAdmin && selectedOrganizationId
            ? apiAdminCompanyProfileUrl(selectedOrganizationId)
            : API_COMPANY_PROFILE_URL

    const publishUrl =
        isWalterAdmin && selectedOrganizationId
            ? apiAdminCompanyPublishUrl(selectedOrganizationId)
            : API_COMPANY_PROFILE_PUBLISH_URL

    const dashboardStatsUrl =
        isWalterAdmin && selectedOrganizationId
            ? apiAdminCompanyDashboardStatsUrl(selectedOrganizationId)
            : API_COMPANY_DASHBOARD_STATS_URL

    const previewInfoUrl =
        isWalterAdmin && selectedOrganizationId
            ? apiAdminCompanyPreviewUrl(selectedOrganizationId)
            : API_COMPANY_PREVIEW_URL

    return (
        <AdminContext.Provider
            value={{
                isWalterAdmin,
                canEdit: true,
                canPublish: true,
                companies,
                isLoadingCompanies,
                selectedOrganizationId,
                selectedCompanyName,
                selectCompany,
                profileUrl,
                publishUrl,
                dashboardStatsUrl,
                previewInfoUrl,
            }}
        >
            {children}
        </AdminContext.Provider>
    )
}

export const useAdmin = () => {
    const context = useContext(AdminContext)
    if (context === undefined) {
        throw new Error('useAdmin must be used within an AdminProvider')
    }
    return context
}
